import { HeaderIcons } from '../shared/icons';
import React, { useState, useEffect, useRef } from 'react';
import { WebSocketAdapter } from '../../utils/WebSocketAdapter';
import Header from '../shared/Header';
import { StandingsSidebar, LiveAnswer, LoadingQuestion, TimerSection, WaitingSpinner, ClueRevealHost } from '../shared';
import { GameEndScreen } from './GameEndScreen';
import type { GameStatistics } from '../../types/game';
import { getGameName, calculateTimeRemaining, isNewQuestion, getQuestionId } from '../../utils/gameUtils';
import { getClueIntervalMs } from '../../constants/gameConstants';
import { useGameEndScreenData } from '../../hooks/useGameEndScreenData';
import '../../App.css';
import '../../styles/game-layout.css';
import { useTranslation } from '../../i18n';

interface ClueQuestion {
  questionId?: string;
  id?: string;
  question: string;
  options: string[];
  clues?: string[];
  timeLimit: number;
  clueIntervalMs?: number;
  questionNumber: number;
  totalQuestions: number;
  startTime: number;
  interesting?: string;
}

interface QuestionResult {
  questionId: string;
  correctAnswer: string;
  submissions: Array<{
    playerId: string;
    answer: string;
    responseTime: number;
    isCorrect: boolean;
    points: number;
  }>;
  leaderboard: Array<{
    playerId: string;
    playerName: string;
    score: number;
  }>;
}

interface WhatCouldItBeHostDisplayProps {
  roomState: {
    roomName: string;
    gameType: string;
    hostId: string;
    players: Array<{ id: string; name: string; score: number; avatar?: string }>;
    gameState: 'waiting' | 'playing' | 'finished';
    currentQuestion?: ClueQuestion;
    questionStartTime?: number;
  };
  socket: WebSocketAdapter;
  onBackToGameSelection?: () => void;
  myPlayerId?: string;
  hostIsPlaying?: boolean;
  onGoHome?: () => void;
  onLoadingTimeout?: () => void;
}

const WhatCouldItBeHostDisplay: React.FC<WhatCouldItBeHostDisplayProps> = ({
  roomState,
  socket,
  onBackToGameSelection,
  hostIsPlaying = false,
  onGoHome,
  onLoadingTimeout,
}) => {
  const { t } = useTranslation();
  const [currentQuestion, setCurrentQuestion] = useState<ClueQuestion | null>(roomState.currentQuestion || null);
  const [timeRemaining, setTimeRemaining] = useState(roomState.currentQuestion?.timeLimit ?? 0);
  const [questionStartTime, setQuestionStartTime] = useState(Date.now());
  const [questionResult, setQuestionResult] = useState<QuestionResult | null>(null);
  const [showDidYouKnow, setShowDidYouKnow] = useState(false);
  const [gameEnded, setGameEnded] = useState(false);
  const [finalLeaderboard, setFinalLeaderboard] = useState<Array<{ playerId: string; playerName: string; score: number }>>([]);
  const [gameStatistics, setGameStatistics] = useState<GameStatistics | null>(null);
  const [waitingForNewGame, setWaitingForNewGame] = useState(false);
  const [liveAnswers, setLiveAnswers] = useState<LiveAnswer[]>([]);
  const [shouldUpdateStandings, setShouldUpdateStandings] = useState(false);

  const [answer, setAnswer] = useState('');
  const [answerSubmitted, setAnswerSubmitted] = useState(false);
  const [answerCorrect, setAnswerCorrect] = useState<boolean | null>(null);
  const [pointsEarned, setPointsEarned] = useState(0);
  const [correctAnswerDisplay, setCorrectAnswerDisplay] = useState<string | null>(null);
  const [acceptedAnswers, setAcceptedAnswers] = useState<string[] | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const isSubmittingRef = useRef(false);
  const previousQuestionIdRef = useRef<string | undefined>(undefined);
  const roomNameRef = useRef(roomState.roomName);

  const gameDisplayName = getGameName(roomState.gameType);
  const gameEndData = useGameEndScreenData(roomState);

  const resetHostPlayingState = () => {
    setAnswer('');
    setAnswerSubmitted(false);
    setAnswerCorrect(null);
    setPointsEarned(0);
    setCorrectAnswerDisplay(null);
    setAcceptedAnswers(null);
    setLiveAnswers([]);
    isSubmittingRef.current = false;
    if (hostIsPlaying) setTimeout(() => inputRef.current?.focus(), 100);
  };

  useEffect(() => {
    roomNameRef.current = roomState.roomName;
  }, [roomState.roomName]);

  // When server says game is finished (e.g. reconnection after missing game-ended), show game end screen
  useEffect(() => {
    if (roomState.gameState === 'finished' && gameEndData) {
      setGameEnded(true);
      setCurrentQuestion(null);
      setFinalLeaderboard(gameEndData.finalLeaderboard);
      setGameStatistics(gameEndData.gameStatistics);
    }
  }, [roomState.gameState, gameEndData]);

  // Sync question and timer from room state (same logic as GameDisplay/useGameState: reconnection + timer sync on refocus)
  useEffect(() => {
    if (roomState.gameState === 'finished') return;
    if (roomState.currentQuestion) {
      const newQuestionId = getQuestionId(roomState.currentQuestion);
      const calculatedTimeRemaining = calculateTimeRemaining(
        roomState.currentQuestion,
        roomState.questionStartTime
      );
      const isNew = isNewQuestion(newQuestionId, previousQuestionIdRef.current);
      const isReconnection = !currentQuestion;

      if (isNew || isReconnection) {
        previousQuestionIdRef.current = newQuestionId;
        setCurrentQuestion(roomState.currentQuestion);
        setTimeRemaining(calculatedTimeRemaining);
        setQuestionStartTime(roomState.questionStartTime ?? Date.now());
        setQuestionResult(null);
        setShowDidYouKnow(false);
        setShouldUpdateStandings(false);
        if (isNew) resetHostPlayingState();
      } else if (newQuestionId === previousQuestionIdRef.current && roomState.gameState === 'playing') {
        // Same question, still playing: sync timer from server (e.g. after tab focus / get-room-state)
        setTimeRemaining(calculatedTimeRemaining);
        setQuestionStartTime((prev) => roomState.questionStartTime ?? prev);
        setCurrentQuestion(roomState.currentQuestion);
      }
    } else if (roomState.gameState === 'waiting') {
      setCurrentQuestion(null);
      setQuestionResult(null);
      setShowDidYouKnow(false);
      setWaitingForNewGame(false);
      setGameEnded(false);
      previousQuestionIdRef.current = undefined;
      resetHostPlayingState();
    }
  }, [roomState.currentQuestion, roomState.questionStartTime, roomState.gameState, currentQuestion, hostIsPlaying]);

  useEffect(() => {
    const handleQuestion = (data: ClueQuestion & { startTime?: number }) => {
      const newId = data.questionId || data.id;
      const serverStartTime = data.startTime ?? Date.now();
      const calculatedRemaining = data.startTime
        ? Math.max(0, data.timeLimit - (Date.now() - data.startTime))
        : data.timeLimit;

      if (newId !== previousQuestionIdRef.current || !currentQuestion) {
        previousQuestionIdRef.current = newId;
        setCurrentQuestion(data);
        setQuestionResult(null);
        setTimeRemaining(calculatedRemaining);
        setQuestionStartTime(serverStartTime);
        resetHostPlayingState();
      } else {
        setCurrentQuestion(data);
        setTimeRemaining(calculatedRemaining);
        setQuestionStartTime(serverStartTime);
        setQuestionResult(null);
        resetHostPlayingState();
      }
    };

    const handleQuestionResult = (data: QuestionResult) => {
      setQuestionResult(data);
      setShowDidYouKnow(false);
      setShouldUpdateStandings(true);
      setTimeout(() => setShouldUpdateStandings(false), 100);
      setTimeout(() => setShowDidYouKnow(true), 1500);
    };

    const handleGameEnded = (data: {
      leaderboard: Array<{ playerId: string; playerName: string; score: number }>;
      statistics?: GameStatistics;
    }) => {
      setGameEnded(true);
      const sortedLeaderboard = [...(data.leaderboard || [])].sort((a, b) => b.score - a.score);
      setFinalLeaderboard(sortedLeaderboard);
      setGameStatistics(data.statistics || null);
      setCurrentQuestion(null);
      setQuestionResult(null);
    };

    const handleGameStarted = () => {
      setWaitingForNewGame(false);
      setGameEnded(false);
      setFinalLeaderboard([]);
      setGameStatistics(null);
      setQuestionResult(null);
      previousQuestionIdRef.current = undefined;
      resetHostPlayingState();
      socket.emit('get-room-state', { roomName: roomNameRef.current });
    };

    const handleAnswerReceived = (data: {
      isCorrect: boolean;
      points: number;
      correctAnswer?: string;
      acceptedAnswers?: string[];
    }) => {
      isSubmittingRef.current = false;
      setAnswerSubmitted(true);
      setAnswerCorrect(data.isCorrect);
      setPointsEarned(data.points);
      if (!data.isCorrect && data.correctAnswer) {
        setCorrectAnswerDisplay(data.correctAnswer);
        setAcceptedAnswers(data.acceptedAnswers ?? null);
      }
    };

    const handlePlayerAnswered = (data: LiveAnswer) => {
      setLiveAnswers((prev) => {
        if (prev.some((a) => a.playerId === data.playerId)) return prev;
        return [...prev, data];
      });
    };

    socket.on('question', handleQuestion);
    socket.on('question-result', handleQuestionResult);
    socket.on('game-ended', handleGameEnded);
    socket.on('game-started', handleGameStarted);
    socket.on('answer-received', handleAnswerReceived);
    socket.on('player-answered', handlePlayerAnswered);

    return () => {
      socket.off('question', handleQuestion);
      socket.off('question-result', handleQuestionResult);
      socket.off('game-ended', handleGameEnded);
      socket.off('game-started', handleGameStarted);
      socket.off('answer-received', handleAnswerReceived);
      socket.off('player-answered', handlePlayerAnswered);
    };
  }, [socket, hostIsPlaying]);

  // Timer countdown (same as CloseOrNot/SpeedChallenge: 100ms tick, snap to 0 at ≤100ms so display shows 0s)
  useEffect(() => {
    if (currentQuestion && timeRemaining > 0 && !questionResult) {
      const timer = setInterval(() => {
        setTimeRemaining((prev) => {
          if (prev <= 100) return 0;
          return prev - 100;
        });
      }, 100);
      return () => clearInterval(timer);
    }
  }, [currentQuestion, timeRemaining, questionResult]);

  if (gameEnded) {
    return (
      <GameEndScreen
        finalLeaderboard={finalLeaderboard.length > 0 ? finalLeaderboard : (gameEndData?.finalLeaderboard ?? [])}
        gameStatistics={gameStatistics ?? gameEndData?.gameStatistics ?? null}
        onPlaySameGame={() => {
          setGameEnded(false);
          setFinalLeaderboard([]);
          setCurrentQuestion(null);
          setQuestionResult(null);
          setWaitingForNewGame(true);
          socket.emit('start-game', { roomName: roomState.roomName });
        }}
        onPlayAnotherGame={() => onBackToGameSelection?.() ?? window.location.reload()}
        onBackToGameSelection={onBackToGameSelection}
        roomName={roomState.roomName}
        gameType={roomState.gameType}
      />
    );
  }

  if (!currentQuestion && !waitingForNewGame && !gameEnded) {
    return (
      <LoadingQuestion
        socket={socket}
        roomName={roomState.roomName}
        title={gameDisplayName}
        subtitle={t('gameplay.loading.question')}
        onGoHome={onGoHome}
        onLongWaitTimeout={onLoadingTimeout}
      />
    );
  }

  if (waitingForNewGame || !currentQuestion) {
    return (
      <div className="app">
        <div className="container">
          <h1 className="title">Starting {gameDisplayName}...</h1>
          <div style={{ textAlign: 'center', marginTop: '2rem' }}>
            <div style={{
              display: 'inline-block',
              width: '40px',
              height: '40px',
              border: '4px solid rgba(248, 250, 252, 0.3)',
              borderTop: '4px solid #F8FAFC',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
            }} />
          </div>
        </div>
      </div>
    );
  }

  const timeSeconds = Math.ceil(timeRemaining / 1000);
  const progress = (timeRemaining / currentQuestion.timeLimit) * 100;
  const clues = currentQuestion.clues ?? [];

  const handleSubmitAnswer = () => {
    if (!answer.trim() || answerSubmitted || isSubmittingRef.current) return;
    const qId = currentQuestion.questionId || currentQuestion.id;
    if (!qId) return;
    isSubmittingRef.current = true;
    setAnswerSubmitted(true);
    socket.emit('submit-answer', {
      roomName: roomState.roomName,
      questionId: qId,
      answer: answer.trim(),
      timestamp: Date.now(),
    });
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSubmitAnswer();
    }
  };

  return (
    <div className="app">
      <Header
        leftButton={{ icon: HeaderIcons.back, onClick: () => onBackToGameSelection?.(), tooltip: t('gameplay.backToGameSelection') }}
        rightButton={{
          icon: HeaderIcons.refresh,
          onClick: () => {
            setWaitingForNewGame(true);
            setGameEnded(false);
            setCurrentQuestion(null);
            resetHostPlayingState();
            socket.emit('start-game', { roomName: roomState.roomName });
          },
          tooltip: t('gameplay.restartGame'),
        }}
        centerContent={
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '1.5rem', flexWrap: 'wrap' }}>
            <div className="header-game-name" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span style={{ fontSize: '0.9rem', color: 'rgba(255, 255, 255, 0.7)' }}>{t('gameplay.partyHost.gameLabel')}</span>
              <span style={{ fontSize: '1.1rem', fontWeight: 'bold', color: '#F8FAFC' }}>
                {gameDisplayName}
              </span>
            </div>
            <div className="header-game-name" style={{ width: '1px', height: '24px', backgroundColor: 'rgba(255, 255, 255, 0.3)' }} />
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span style={{ fontSize: '0.9rem', color: 'rgba(255, 255, 255, 0.7)' }}>{t('gameplay.partyHost.roomCodeLabel')}</span>
              <div style={{
                fontSize: '1.3rem',
                fontWeight: 'bold',
                letterSpacing: '0.15rem',
                color: 'var(--color-primary)',
                padding: '0.3rem 0.8rem',
                backgroundColor: 'rgba(var(--color-primary-rgb), 0.1)',
                borderRadius: '6px',
                border: '2px solid var(--color-primary)',
              }}>
                {roomState.roomName}
              </div>
            </div>
          </div>
        }
      />

      <div className="game-host-layout">
        <div className={`game-host-left no-image ${!hostIsPlaying ? 'host-only' : ''}`}>
          <div className="game-host-timer-section">
            <TimerSection
              questionNumber={currentQuestion.questionNumber}
              totalQuestions={currentQuestion.totalQuestions}
              timeSeconds={timeSeconds}
              progress={progress}
            />
          </div>

          <div className="game-host-question-section game-host-question-section--with-clues">
            <div className="game-display-centered-content" style={{ width: '100%' }}>
                  <h1 className="game-question-text" style={{ color: 'var(--color-primary)', marginBottom: '1rem', letterSpacing: '0.03em', textShadow: '0 2px 8px rgba(var(--color-primary-rgb), 0.3)' }}>
                    {currentQuestion.question}
                  </h1>

                  <ClueRevealHost clues={clues} startTime={questionStartTime} clueIntervalMs={currentQuestion.clueIntervalMs ?? getClueIntervalMs(currentQuestion.timeLimit)} themeColor="var(--color-primary)" />

                  {questionResult && (
                    <div className="wcib-result-card-wrap" style={{ marginTop: '2rem', width: '100%' }}>
                      <div className="wcib-result-card" style={{
                        display: 'grid',
                        gridTemplateRows: '1fr',
                        gridTemplateColumns: '1fr',
                        transformStyle: 'preserve-3d',
                        transition: 'transform 0.8s cubic-bezier(0.4, 0, 0.2, 1)',
                        transform: showDidYouKnow && currentQuestion.interesting ? 'rotateY(180deg)' : 'rotateY(0deg)',
                      }}>
                        <div style={{
                          gridRow: 1,
                          gridColumn: 1,
                          padding: '1.5rem 2rem',
                          backgroundColor: 'rgba(var(--color-primary-rgb), 0.2)',
                          border: '3px solid var(--color-primary)',
                          borderRadius: '15px',
                          textAlign: 'center',
                          backfaceVisibility: 'hidden',
                          alignSelf: 'start',
                        }}>
                          <p style={{ fontSize: '1.2rem', color: 'rgba(255,255,255,0.7)', margin: 0 }}>{t('gameplay.speedChallenge.correctAnswerColon')}</p>
                          <p style={{ fontSize: '2rem', color: 'var(--color-primary)', fontWeight: 'bold', margin: '0.5rem 0 0 0' }}>
                            {questionResult.correctAnswer}
                          </p>
                        </div>
                        {currentQuestion.interesting && (
                          <div className="wcib-did-you-know" style={{
                            gridRow: 1,
                            gridColumn: 1,
                            padding: '1.5rem 2rem',
                            backgroundColor: 'rgba(var(--color-primary-rgb), 0.2)',
                            border: '3px solid var(--color-primary)',
                            borderRadius: '15px',
                            textAlign: 'center',
                            backfaceVisibility: 'hidden',
                            transform: 'rotateY(180deg)',
                            alignSelf: 'start',
                          }}>
                            <p className="wcib-did-you-know-title" style={{ color: 'var(--color-primary)', fontWeight: 'bold', margin: '0 0 0.8rem 0' }}>💡 Did you know?</p>
                            <p className="wcib-did-you-know-body" style={{ color: '#fff', lineHeight: '1.6', margin: 0 }}>{currentQuestion.interesting}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {hostIsPlaying && !answerSubmitted && (
                    <div style={{ marginTop: '2rem', width: '100%', maxWidth: '600px', margin: '2rem auto 0' }}>
                      <input
                        ref={inputRef}
                        type="text"
                        value={answer}
                        onChange={(e) => setAnswer(e.target.value)}
                        onKeyDown={handleKeyDown}
                        placeholder={t('gameplay.typeYourAnswer')}
                        autoComplete="off"
                        style={{
                          width: '100%',
                          padding: '1.2rem',
                          fontSize: 'clamp(1rem, 2.5vw, 1.5rem)',
                          textAlign: 'center',
                          backgroundColor: 'rgba(248, 250, 252, 0.1)',
                          border: '3px solid rgba(var(--color-primary-rgb), 0.4)',
                          borderRadius: '15px',
                          color: '#fff',
                          outline: 'none',
                        }}
                      />
                      <button
                        onClick={handleSubmitAnswer}
                        disabled={!answer.trim()}
                        className="button"
                        style={{
                          width: '100%',
                          marginTop: '1rem',
                          backgroundColor: answer.trim() ? 'var(--color-primary)' : 'rgba(248, 250, 252, 0.3)',
                          opacity: answer.trim() ? 1 : 0.5,
                          cursor: answer.trim() ? 'pointer' : 'not-allowed',
                        }}
                      >
                        {t('gameplay.submitAnswer')}
                      </button>
                    </div>
                  )}

                  {hostIsPlaying && answerSubmitted && (
                    <div style={{ marginTop: '2rem', width: '100%', maxWidth: '600px', margin: '2rem auto 0' }}>
                      <div style={{
                        padding: '1.5rem',
                        backgroundColor: answerCorrect ? 'rgba(var(--color-primary-rgb), 0.2)' : 'rgba(239, 68, 68, 0.2)',
                        border: `2px solid ${answerCorrect ? 'var(--color-primary)' : '#EF4444'}`,
                        borderRadius: '15px',
                        textAlign: 'center',
                        color: '#fff',
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}>
                          <span style={{ fontSize: '2rem', fontWeight: 'bold' }}>{answerCorrect ? '✓' : '✗'}</span>
                          <span style={{ fontSize: '1.3rem', fontWeight: 'bold' }}>
                            {answerCorrect ? `Correct! (+${pointsEarned})` : 'Wrong Answer'}
                          </span>
                        </div>
                        {!answerCorrect && correctAnswerDisplay && (
                          <div style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid rgba(239, 68, 68, 0.3)' }}>
                            <p style={{ fontSize: '0.9rem', color: 'rgba(255,255,255,0.6)', margin: 0 }}>{t('gameplay.speedChallenge.correctAnswerLower')}</p>
                            <p style={{ fontSize: '1.2rem', color: '#fff', fontWeight: 'bold', margin: '0.3rem 0' }}>{correctAnswerDisplay}</p>
                            {acceptedAnswers && acceptedAnswers.length > 1 && (
                              <p style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.5)', margin: '0.5rem 0 0 0' }}>
                                Also accepted: {acceptedAnswers.filter((a) => a !== correctAnswerDisplay).join(', ')}
                              </p>
                            )}
                          </div>
                        )}
                        <p style={{ fontSize: '0.9rem', color: 'rgba(255,255,255,0.6)', margin: '1rem 0 0 0' }}>
                          Your answer: <span style={{ fontWeight: 'bold' }}>{answer}</span>
                        </p>
                      </div>
                      <WaitingSpinner isLoading={false} />
                    </div>
                  )}
            </div>
          </div>
        </div>

        <div className="game-host-right">
          <StandingsSidebar
            players={roomState.players}
            liveAnswers={liveAnswers}
            totalPlayers={roomState.players.length}
            questionId={currentQuestion?.questionId || currentQuestion?.id}
            shouldUpdateScores={shouldUpdateStandings}
            theme={{
              primary: 'var(--color-primary)',
              primaryLight: 'rgba(var(--color-primary-rgb), 0.15)',
              accent: '#F59E0B',
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default WhatCouldItBeHostDisplay;
